import React, { useState, useEffect } from 'react';
import { X, Upload, Loader2 } from 'lucide-react';

const ProductCategoryLabels = {
  1: 'Merchandise',
  2: 'Supplement',
  3: 'Accessory',
  4: 'Voucher',
  5: 'Other'
};

const StockStatusLabels = {
  1: 'In Stock',
  2: 'Out Of Stock',
  3: 'Limited Stock'
};

const ProductFormModal = ({ isOpen, onClose, onSave, product, gyms }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [coinCost, setCoinCost] = useState(0);
  const [category, setCategory] = useState(1);
  const [stockStatus, setStockStatus] = useState(1);
  const [stockQuantity, setStockQuantity] = useState('');
  const [gymId, setGymId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mrpPrice, setMrpPrice] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [bonusOffer, setBonusOffer] = useState('');
  const [additionalImageUrls, setAdditionalImageUrls] = useState([]);

  // Helper: normalize category string to numeric (API now returns strings)
  const normCategory = (cat) => {
    if (typeof cat === 'number') return cat;
    const map = { Merchandise: 1, Supplement: 2, Accessory: 3, Voucher: 4, Other: 5 };
    return map[cat] || 1;
  };
  // Helper: normalize stockStatus string to numeric
  const normStockStatus = (s) => {
    if (typeof s === 'number') return s;
    const map = { InStock: 1, OutOfStock: 2, LimitedStock: 3 };
    return map[s] || 1;
  };

  useEffect(() => {
    if (product) {
      setName(product.name || '');
      setDescription(product.description || '');
      setCoinCost(product.coinCost || 0);
      setCategory(normCategory(product.category));
      setStockStatus(normStockStatus(product.stockStatus));
      setStockQuantity(product.stockQuantity !== null && product.stockQuantity !== undefined ? product.stockQuantity : '');
      setGymId(product.gymId || '');
      setIsActive(product.isActive !== undefined ? product.isActive : true);
      setImageUrl(product.imageUrl || '');
      setImagePreview(product.imageUrl || '');
      setImageFile(null);
      setMrpPrice(product.mrpPrice !== null && product.mrpPrice !== undefined ? product.mrpPrice : '');
      setSellingPrice(product.sellingPrice !== null && product.sellingPrice !== undefined ? product.sellingPrice : '');
      setBonusOffer(product.bonusOffer || '');
      setAdditionalImageUrls(product.images ? product.images.map(img => img.imageUrl) : []);
    } else {
      setName('');
      setDescription('');
      setCoinCost(100);
      setCategory(1);
      setStockStatus(1);
      setStockQuantity('');
      setGymId('');
      setIsActive(true);
      setImageUrl('');
      setImagePreview('');
      setImageFile(null);
      setMrpPrice('');
      setSellingPrice('');
      setBonusOffer('');
      setAdditionalImageUrls([]);
    }
    setError('');
  }, [product, isOpen]);


  const handleAddImageUrl = () => {
    setAdditionalImageUrls([...additionalImageUrls, '']);
  };

  const handleRemoveImageUrl = (index) => {
    const updated = additionalImageUrls.filter((_, i) => i !== index);
    setAdditionalImageUrls(updated);
  };

  const handleImageUrlChange = (index, value) => {
    const updated = [...additionalImageUrls];
    updated[index] = value;
    setAdditionalImageUrls(updated);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setError('');

      // Create local preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Product Name is required.');
      return;
    }
    if (coinCost <= 0) {
      setError('Coin Cost must be greater than zero.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('Name', name.trim());
      formData.append('Description', description.trim());
      formData.append('CoinCost', parseInt(coinCost, 10));
      formData.append('Category', parseInt(category, 10));
      formData.append('StockStatus', parseInt(stockStatus, 10));

      if (stockQuantity !== '') {
        formData.append('StockQuantity', parseInt(stockQuantity, 10));
      }

      if (gymId) {
        formData.append('GymId', gymId);
      }

      formData.append('IsActive', isActive);

      if (imageFile) {
        formData.append('ImageFile', imageFile);
      } else {
        formData.append('ImageUrl', imageUrl);
      }

      if (mrpPrice !== '') {
        formData.append('MrpPrice', parseFloat(mrpPrice));
      }
      if (sellingPrice !== '') {
        formData.append('SellingPrice', parseFloat(sellingPrice));
      }
      if (bonusOffer.trim()) {
        formData.append('BonusOffer', bonusOffer.trim());
      }
      additionalImageUrls.forEach((url) => {
        if (url.trim()) {
          formData.append('AdditionalImageUrls', url.trim());
        }
      });

      await onSave(formData);
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to save product.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-[#0F172A] border border-[#1E293B] rounded-xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">

        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1E293B] bg-[#0B0F19]">
          <h3 className="text-lg font-bold text-white tracking-wide">
            {product ? 'Edit Store Product' : 'Add New Product'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-xs font-semibold">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Product Name */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Product Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. FitScan Premium Shaker"
                className="w-full bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                required
              />
            </div>

            {/* Coin Cost */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Coin Cost *</label>
              <input
                type="number"
                value={coinCost}
                onChange={(e) => setCoinCost(e.target.value)}
                placeholder="e.g. 250"
                className="w-full bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Category *</label>
              <select
                value={category}
                onChange={(e) => setCategory(parseInt(e.target.value, 10))}
                className="w-full bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
              >
                {Object.entries(ProductCategoryLabels).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>

            {/* Gym Affiliation */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Gym Availability</label>
              <select
                value={gymId}
                onChange={(e) => setGymId(e.target.value)}
                className="w-full bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
              >
                <option value="">Global (Available at all Gyms)</option>
                {gyms.map((g) => (
                  <option key={g.id} value={g.id}>{g.name} ({g.code})</option>
                ))}
              </select>
            </div>

            {/* Stock Status */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Stock Status</label>
              <select
                value={stockStatus}
                onChange={(e) => setStockStatus(parseInt(e.target.value, 10))}
                className="w-full bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
              >
                {Object.entries(StockStatusLabels).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>

            {/* Stock Quantity */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Stock Quantity (Optional)</label>
              <input
                type="number"
                value={stockQuantity}
                onChange={(e) => setStockQuantity(e.target.value)}
                placeholder="Leave blank for infinite"
                className="w-full bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
              />
            </div>

            {/* MRP (₹) */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">MRP (₹) (Optional)</label>
              <input
                type="number"
                step="0.01"
                value={mrpPrice}
                onChange={(e) => setMrpPrice(e.target.value)}
                placeholder="e.g. 499.00"
                className="w-full bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
              />
            </div>

            {/* Selling Price (₹) */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Selling Price (₹) (Optional)</label>
              <input
                type="number"
                step="0.01"
                value={sellingPrice}
                onChange={(e) => setSellingPrice(e.target.value)}
                placeholder="e.g. 399.00"
                className="w-full bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
              />
            </div>

            {/* Bonus Offer */}
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Bonus Offer Text (Optional)</label>
              <input
                type="text"
                value={bonusOffer}
                onChange={(e) => setBonusOffer(e.target.value)}
                placeholder="e.g. +150 coins on 60-day streak"
                className="w-full bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide details about size, flavor, benefits..."
              rows={3}
              className="w-full bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800] resize-none"
            />
          </div>

          {/* Image Upload / Preview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-gray-800/60">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Product Image</label>

              {/* File Upload Button */}
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border border-dashed border-gray-700 rounded-lg cursor-pointer bg-[#0B0F19] hover:bg-[#0B0F19]/80 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 mb-2 text-gray-400" />
                    <p className="text-xs text-gray-400"><span className="font-semibold text-[#6CD800]">Click to upload</span> image file</p>
                    <p className="text-[10px] text-gray-500 mt-1">PNG, JPG, WEBP</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </label>
              </div>

              {/* Or enter Image URL */}
              <div className="mt-3">
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Or enter image URL</label>
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    setImagePreview(e.target.value);
                  }}
                  placeholder="https://example.com/image.jpg"
                  className="w-full bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                />
              </div>
            </div>

            {/* Preview Container */}
            <div className="flex flex-col items-center justify-center bg-[#0B0F19] border border-gray-800 rounded-lg p-4 h-full min-h-[160px]">
              <span className="text-xs font-semibold text-gray-500 mb-2">Image Preview</span>
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Product preview"
                  className="max-h-28 object-contain rounded border border-gray-800 bg-[#0F172A]"
                />
              ) : (
                <div className="flex items-center justify-center w-full h-full text-gray-600 text-xs">
                  No image selected
                </div>
              )}
            </div>
          </div>

          {/* Additional Image URLs */}
          <div className="pt-4 border-t border-gray-800/60 space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider">Additional Image URLs for Carousel (Optional)</label>
              <button
                type="button"
                onClick={handleAddImageUrl}
                className="text-xs font-bold text-[#6CD800] hover:text-[#5bc200] transition-colors cursor-pointer"
              >
                + Add Image URL
              </button>
            </div>

            {additionalImageUrls.length === 0 ? (
              <p className="text-[11px] text-gray-500 italic">No additional images added yet. Click "+ Add Image URL" to add.</p>
            ) : (
              <div className="space-y-2.5">
                {additionalImageUrls.map((url, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={url}
                      onChange={(e) => handleImageUrlChange(idx, e.target.value)}
                      placeholder="https://example.com/additional-image.jpg"
                      className="flex-1 bg-[#1E293B] text-white border border-gray-800 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImageUrl(idx)}
                      className="px-2.5 py-1.5 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 text-red-400 rounded-lg text-xs font-bold transition-all cursor-pointer"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active status */}
          <div className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded bg-[#1E293B] border-gray-700 text-[#6CD800] focus:ring-[#6CD800]/50 focus:ring-offset-0 focus:ring-1"
            />
            <label htmlFor="isActive" className="text-xs font-bold text-gray-300 cursor-pointer block select-none">
              Publish Product (Make active and visible in member store)
            </label>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#1E293B] bg-[#0B0F19]">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-700 hover:bg-[#1E293B] text-gray-300 text-sm font-semibold rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2 bg-[#6CD800] text-[#0B0F19] text-sm font-bold rounded-lg hover:bg-[#5bc200] disabled:opacity-50 flex items-center gap-1.5 transition-colors"
          >
            {saving && <Loader2 className="animate-spin" size={16} />}
            {product ? 'Save Changes' : 'Create Product'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ProductFormModal;
export { ProductCategoryLabels, StockStatusLabels };
